import { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import StandLayoutVisualizer from './StandLayoutVisualizer';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const CELL_SIZE = 8; // px per cell in miniature grid

/* ─────────────────────────────────────────────
   Mini hall SVG — shows one hall with all its
   stands. Selected ones are highlighted.
───────────────────────────────────────────── */
function HallMiniGrid({ hall, stands, selected, onToggle, maxReached, zoom, onWheelZoom }) {
  const W = hall.width || 50;
  const H = hall.height || 50;
  const cell = CELL_SIZE * zoom;

  // Group stands by hall
  const hallStands = stands.filter(s => s.hall_id === hall.id);

  return (
    <div
      className="overflow-auto rounded-xl border border-gray-200 bg-gray-50 p-2 max-h-[60vh]"
      onWheel={onWheelZoom}
      data-testid="stand-picker-grid-scroller"
    >
      <p className="text-xs font-bold text-gray-600 mb-1 px-1">{hall.name}</p>
      <svg
        width={W * cell}
        height={H * cell}
        viewBox={`0 0 ${W * CELL_SIZE} ${H * CELL_SIZE}`}
        style={{ display: 'block' }}
      >
        {/* Grid background */}
        {Array.from({ length: H }, (_, r) =>
          Array.from({ length: W }, (_, c) => (
            <rect
              key={`bg-${r}-${c}`}
              x={c * CELL_SIZE} y={r * CELL_SIZE}
              width={CELL_SIZE} height={CELL_SIZE}
              fill="#f8fafc" stroke="#e2e8f0" strokeWidth="0.3"
            />
          ))
        )}

        {/* Stands */}
        {hallStands.map(stand => {
          const isSelected = selected.some(s => s.stand_id === stand.stand_id);
          const isAssigned  = stand.status === 'reserved' || stand.status === 'unavailable';
          const isDisabled  = isAssigned || (maxReached && !isSelected);

          const color = isSelected
            ? '#f59e0b'
            : isAssigned ? '#94a3b8'
            : '#22c55e';

          return stand.cells.map((cell, ci) => (
            <rect
              key={`${stand.stand_id}-${ci}`}
              x={cell.col * CELL_SIZE}
              y={cell.row * CELL_SIZE}
              width={CELL_SIZE}
              height={CELL_SIZE}
              fill={color}
              fillOpacity={isDisabled && !isSelected ? 0.4 : 0.85}
              stroke={isSelected ? '#d97706' : '#fff'}
              strokeWidth={isSelected ? 0.8 : 0.2}
              style={{ cursor: isDisabled ? 'not-allowed' : 'pointer' }}
              onClick={() => !isAssigned && onToggle(stand)}
            />
          ));
        })}

        {/* Stand labels */}
        {hallStands.map(stand => {
          if (!stand.cells.length) return null;
          const minC = Math.min(...stand.cells.map(c => c.col));
          const maxC = Math.max(...stand.cells.map(c => c.col));
          const minR = Math.min(...stand.cells.map(c => c.row));
          const maxR = Math.max(...stand.cells.map(c => c.row));
          const cx = ((minC + maxC + 1) / 2) * CELL_SIZE;
          const cy = ((minR + maxR + 1) / 2) * CELL_SIZE;
          const isSelected = selected.some(s => s.stand_id === stand.stand_id);
          return (
            <text
              key={`lbl-${stand.stand_id}`}
              x={cx} y={cy + 1.5}
              textAnchor="middle"
              fontSize={Math.min(CELL_SIZE * 0.65, 7)}
              fill={isSelected ? '#7c2d12' : '#fff'}
              fontWeight="600"
              pointerEvents="none"
              style={{ userSelect: 'none' }}
            >
              {stand.stand_no}
            </text>
          );
        })}
      </svg>
      {/* Legend */}
      <div className="flex items-center gap-3 mt-1 px-1">
        <span className="flex items-center gap-1 text-xs text-gray-500">
          <span className="inline-block w-3 h-3 bg-green-500 rounded-sm" /> Müsait
        </span>
        <span className="flex items-center gap-1 text-xs text-gray-500">
          <span className="inline-block w-3 h-3 bg-amber-400 rounded-sm" /> Seçili
        </span>
        <span className="flex items-center gap-1 text-xs text-gray-500">
          <span className="inline-block w-3 h-3 bg-slate-400 rounded-sm" /> Dolu
        </span>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   Selected stand mini SVG card (selected list)
───────────────────────────────────────────── */
function SelectedStandCard({ stand, onRemove, idx, language }) {
  const cells = stand.cells || [];
  if (!cells.length) return null;

  return (
    <div className="flex items-start gap-2 bg-amber-50 border border-amber-300 rounded-xl px-3 py-2">
      <div className="shrink-0">
        <StandLayoutVisualizer
          cells={cells}
          openSides={stand.open_sides || []}
          standNo={stand.stand_no || ''}
          cellSize={14}
          compact
        />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-bold text-amber-800">
          {language === 'tr' ? `${idx + 1}. Seçenek` : `Option ${idx + 1}`}
          <span className="font-mono ml-1 bg-amber-200 px-1 rounded">{stand.stand_no}</span>
        </p>
        <p className="text-xs text-gray-600">{stand.hall_name}</p>
        {stand.area_sqm && <p className="text-xs text-gray-500">{stand.area_sqm} m²</p>}
      </div>
      <button onClick={() => onRemove(stand)} className="shrink-0 text-red-400 hover:text-red-600 text-sm ml-1">✕</button>
    </div>
  );
}

/* ─────────────────────────────────────────────
   Main Modal
───────────────────────────────────────────── */
export default function StandPickerModal({ language, onClose, onPropose, adminNote, setAdminNote, mode = 'propose' }) {
  const tr = language === 'tr';
  // mode='direct' (Item 3) → admin re-assigns immediately, max 1 stand. UI
  // mirrors the propose modal but caps selection and changes labels.
  const isDirect = mode === 'direct';
  const MAX_SELECT = isDirect ? 1 : 3;

  const [halls, setHalls] = useState([]);
  const [stands, setStands] = useState([]);
  const [activeHallIdx, setActiveHallIdx] = useState(0);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(true);
  const [zoom, setZoom] = useState(1);

  const ZOOM_MIN = 0.5;
  const ZOOM_MAX = 4;
  const ZOOM_STEP = 0.25;
  const clampZoom = (z) => Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, Number(z.toFixed(2))));
  const zoomIn   = () => setZoom(z => clampZoom(z + ZOOM_STEP));
  const zoomOut  = () => setZoom(z => clampZoom(z - ZOOM_STEP));
  const zoomReset = () => setZoom(1);

  // Ctrl/Cmd + wheel inside grid → zoom (does not scroll page)
  const onWheelZoom = useCallback((e) => {
    if (!(e.ctrlKey || e.metaKey)) return;
    e.preventDefault();
    setZoom(z => clampZoom(z + (e.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP)));
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('cop31_ultra_token');
    Promise.all([
      axios.get(`${API}/ultra-admin/halls`,           { headers: { Authorization: `Bearer ${token}` } }),
      axios.get(`${API}/ultra-admin/available-stands`, { headers: { Authorization: `Bearer ${token}` } }),
    ]).then(([hRes, sRes]) => {
      setHalls(hRes.data || []);
      setStands(sRes.data || []);
    }).finally(() => setLoading(false));
  }, []);

  const toggleStand = useCallback((stand) => {
    setSelected(prev => {
      const existing = prev.findIndex(s => s.stand_id === stand.stand_id);
      if (existing >= 0) return prev.filter(s => s.stand_id !== stand.stand_id);
      if (prev.length >= MAX_SELECT) return isDirect ? [stand] : prev; // direct: replace; propose: ignore over cap
      return [...prev, stand];
    });
  }, [MAX_SELECT, isDirect]);

  const activeHall = halls[activeHallIdx];
  const maxReached = selected.length >= MAX_SELECT;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-base font-bold text-gray-800">
              {tr ? 'Grid Planından Stand Seç' : 'Select Stands from Grid Plan'}
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">
              {tr ? 'Yeşil standlara tıklayarak max 3 seçin, ardından müşteriye önerin.' : 'Click green stands to select up to 3, then propose to customer.'}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl font-light">✕</button>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Left: Hall grid */}
          <div className="flex-1 overflow-auto p-4">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <div className="animate-spin h-8 w-8 border-4 border-amber-400 border-t-transparent rounded-full" />
              </div>
            ) : (
              <>
                {/* Hall tabs + Zoom controls */}
                <div className="flex items-center gap-2 mb-3">
                  <div className="flex gap-1 overflow-x-auto pb-1 flex-1">
                    {halls.map((h, i) => (
                      <button key={h.id}
                        onClick={() => setActiveHallIdx(i)}
                        className={`shrink-0 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                          i === activeHallIdx
                            ? 'bg-amber-500 text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}>
                        {h.name}
                      </button>
                    ))}
                  </div>
                  <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-lg px-1 py-0.5 shadow-sm shrink-0" data-testid="stand-picker-zoom-controls">
                    <button
                      type="button"
                      onClick={zoomOut}
                      disabled={zoom <= ZOOM_MIN}
                      className="w-7 h-7 flex items-center justify-center rounded-md text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed text-lg leading-none"
                      data-testid="stand-picker-zoom-out"
                      title={tr ? 'Uzaklaştır (Ctrl + scroll)' : 'Zoom out (Ctrl + scroll)'}
                    >
                      −
                    </button>
                    <button
                      type="button"
                      onClick={zoomReset}
                      className="min-w-[44px] px-1 h-7 text-[11px] font-mono font-semibold text-gray-700 hover:bg-gray-100 rounded-md"
                      data-testid="stand-picker-zoom-reset"
                      title={tr ? 'Sıfırla' : 'Reset'}
                    >
                      {Math.round(zoom * 100)}%
                    </button>
                    <button
                      type="button"
                      onClick={zoomIn}
                      disabled={zoom >= ZOOM_MAX}
                      className="w-7 h-7 flex items-center justify-center rounded-md text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed text-lg leading-none"
                      data-testid="stand-picker-zoom-in"
                      title={tr ? 'Yakınlaştır (Ctrl + scroll)' : 'Zoom in (Ctrl + scroll)'}
                    >
                      +
                    </button>
                  </div>
                </div>

                {/* Hall grid */}
                {activeHall && (
                  <HallMiniGrid
                    hall={activeHall}
                    stands={stands}
                    selected={selected}
                    onToggle={toggleStand}
                    maxReached={maxReached}
                    zoom={zoom}
                    onWheelZoom={onWheelZoom}
                  />
                )}
              </>
            )}
          </div>

          {/* Right: Selected stands */}
          <div className="w-72 border-l border-gray-100 p-4 flex flex-col gap-3 overflow-y-auto">
            <div className="flex items-center justify-between">
              <p className="text-xs font-bold text-gray-700">
                {tr ? 'Seçilen Standlar' : 'Selected Stands'}
                <span className={`ml-1 px-1.5 py-0.5 rounded-full text-xs ${maxReached ? 'bg-amber-500 text-white' : 'bg-gray-200 text-gray-600'}`}>
                  {selected.length}/{MAX_SELECT}
                </span>
              </p>
              {selected.length > 0 && (
                <button onClick={() => setSelected([])}
                  className="text-xs text-red-400 hover:text-red-600">
                  {tr ? 'Temizle' : 'Clear'}
                </button>
              )}
            </div>

            {selected.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center text-center p-4">
                <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mb-3">
                  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2"><path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
                </div>
                <p className="text-xs text-gray-400">
                  {tr ? 'Grid planından standlara tıklayın' : 'Click stands from the grid plan'}
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {selected.map((s, i) => (
                  <SelectedStandCard
                    key={s.stand_id}
                    stand={s}
                    idx={i}
                    language={language}
                    onRemove={toggleStand}
                  />
                ))}
              </div>
            )}

            {/* Admin note */}
            {selected.length > 0 && (
              <div className="mt-auto">
                <label className="text-xs text-gray-500 mb-1 block">
                  {tr ? 'Admin notu (isteğe bağlı)' : 'Admin note (optional)'}
                </label>
                <textarea
                  value={adminNote}
                  onChange={e => setAdminNote(e.target.value)}
                  rows={2}
                  className="w-full text-xs border border-gray-300 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-amber-400 resize-none"
                  placeholder={tr ? 'Müşteriye not...' : 'Note to customer...'}
                />
              </div>
            )}

            {/* Action buttons */}
            <div className="flex flex-col gap-2 mt-2">
              <button
                disabled={selected.length === 0}
                onClick={() => onPropose(selected)}
                data-testid={isDirect ? 'stand-picker-direct-confirm' : 'stand-picker-propose-confirm'}
                className={`w-full py-2.5 rounded-xl ${
                  isDirect
                    ? 'bg-violet-600 hover:bg-violet-700 disabled:bg-gray-200 disabled:text-gray-400'
                    : 'bg-amber-500 hover:bg-amber-600 disabled:bg-gray-200 disabled:text-gray-400'
                } text-white text-xs font-bold transition-colors`}>
                {isDirect
                  ? (tr
                      ? `${selected.length > 0 ? selected.length + ' Standı ' : ''}Direkt Ata`
                      : `Direct Re-assign${selected.length > 0 ? ' ' + selected.length + ' Stand' : ''}`)
                  : (tr
                      ? `${selected.length > 0 ? selected.length + ' Standu ' : ''}Müşteriye Öner`
                      : `Propose ${selected.length > 0 ? selected.length + ' Stand(s) ' : ''}to Customer`)}
              </button>
              <button onClick={onClose}
                className="w-full py-2 text-xs text-gray-400 hover:text-gray-600 transition-colors">
                {tr ? 'İptal' : 'Cancel'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
